/**
 * Mock data for Smart Conflicts Detector
 * 
 * TODO: Remove this file after backend API implementation is complete.
 * These mocks are only for UI development and testing purposes.
 */

import { 
  IConflict, 
  IConflictStats, 
  IAutoResolveSettings 
} from '../types/conflictsDetector.types';
import { ITherapist } from '../types/appointments.types';

// Mock therapists for conflicts detector
const mockTherapists: ITherapist[] = [
  { id: 1, name: 'ד"ר יעל כהן', isActive: true },
  { id: 2, name: 'ד"ר דוד לוי', isActive: true },
  { id: 3, name: 'ד"ר שרה אברהם', isActive: true },
];

/**
 * Mock conflicts statistics
 */
export const mockConflictStats: IConflictStats = {
  critical: 3,
  high: 2,
  medium: 2,
  autoResolved: 12,
  autoResolvedPeriod: "CONFLICTS_THIS_WEEK"
};

/**
 * Mock auto-resolve settings
 */
export const mockAutoResolveSettings: IAutoResolveSettings = {
  enabled: true,
  options: [
    {
      id: "duplicates",
      titleKey: "CONFLICTS_AUTO_OPTION_DUPLICATES_TITLE",
      descriptionKey: "CONFLICTS_AUTO_OPTION_DUPLICATES_DESC",
      enabled: true
    },
    {
      id: "rooms",
      titleKey: "CONFLICTS_AUTO_OPTION_ROOMS_TITLE",
      descriptionKey: "CONFLICTS_AUTO_OPTION_ROOMS_DESC",
      enabled: true
    },
    {
      id: "holidays",
      titleKey: "CONFLICTS_AUTO_OPTION_HOLIDAYS_TITLE",
      descriptionKey: "CONFLICTS_AUTO_OPTION_HOLIDAYS_DESC",
      enabled: false
    },
    {
      id: "notifications",
      titleKey: "CONFLICTS_AUTO_OPTION_NOTIFICATIONS_TITLE",
      descriptionKey: "CONFLICTS_AUTO_OPTION_NOTIFICATIONS_DESC",
      enabled: true
    }
  ]
};

/**
 * Mock conflicts list
 */
export const mockConflicts: IConflict[] = [
  // Critical: Therapist double booking
  {
    id: "conflict-1",
    type: "double-booking-therapist",
    severity: "critical",
    typeLabel: "CONFLICTS_TYPE_THERAPIST_DOUBLE_BOOKING",
    title: "CONFLICTS_THERAPIST_DOUBLE_BOOKING_TITLE",
    description: "CONFLICTS_THERAPIST_DOUBLE_BOOKING_DESC",
    date: new Date(2025, 11, 25), // Dec 25, 2025
    timeRange: "10:00-11:00",
    details: [
      { label: "CONFLICTS_DETAIL_DATE_TIME", value: "CONFLICTS_DETAIL_DATE_TIME_VALUE_1" },
      { label: "CONFLICTS_DETAIL_APPOINTMENT_1", value: "אבי מזרחי • חדר 1A" },
      { label: "CONFLICTS_DETAIL_APPOINTMENT_2", value: "דנה כהן • חדר 3B" },
      { label: "CONFLICTS_DETAIL_TIME_UNTIL", value: "48 שעות", highlight: "danger" }
    ],
    appointments: [
      {
        id: "appt-1a",
        patientName: "אבי מזרחי",
        therapist: mockTherapists[0], // Dr. Moshe Cohen
        time: "10:00",
        room: "1A",
        date: new Date(2025, 11, 25)
      },
      {
        id: "appt-1b",
        patientName: "דנה כהן",
        therapist: mockTherapists[0], // Dr. Moshe Cohen
        time: "10:00",
        room: "3B",
        date: new Date(2025, 11, 25)
      }
    ],
    suggestedSolutions: [
      {
        id: "sol-1a",
        title: "CONFLICTS_SOLUTION_TRANSFER_PATIENT",
        description: "CONFLICTS_SOLUTION_TRANSFER_PATIENT_DESC",
        score: 95,
        isRecommended: true,
        impacts: [
          { text: "CONFLICTS_IMPACT_SAME_TIME", isPositive: true },
          { text: "CONFLICTS_IMPACT_SAME_TREATMENT", isPositive: true },
          { text: "CONFLICTS_IMPACT_SAME_INSURANCE", isPositive: true }
        ]
      },
      {
        id: "sol-1b",
        title: "CONFLICTS_SOLUTION_RESCHEDULE_TIME",
        description: "CONFLICTS_SOLUTION_RESCHEDULE_TIME_DESC",
        score: 78,
        isRecommended: false,
        impacts: [
          { text: "CONFLICTS_IMPACT_SAME_THERAPIST", isPositive: true },
          { text: "CONFLICTS_IMPACT_DIFFERENT_TIME", isPositive: false }
        ]
      }
    ]
  },
  // Critical: Room double booking
  {
    id: "conflict-2",
    type: "double-booking-room",
    severity: "critical",
    typeLabel: "CONFLICTS_TYPE_ROOM_DOUBLE_BOOKING",
    title: "CONFLICTS_ROOM_DOUBLE_BOOKING_TITLE",
    description: "CONFLICTS_ROOM_DOUBLE_BOOKING_DESC",
    date: new Date(2025, 11, 26), // Dec 26, 2025
    timeRange: "14:00-15:00",
    details: [
      { label: "CONFLICTS_DETAIL_DATE_TIME", value: "CONFLICTS_DETAIL_DATE_TIME_VALUE_2" },
      { label: "CONFLICTS_DETAIL_SESSION_1", value: 'ד"ר רחל מזרחי • רונית לוי' },
      { label: "CONFLICTS_DETAIL_SESSION_2", value: 'ד"ר דוד אברהם • יוסי כהן' },
      { label: "CONFLICTS_DETAIL_AVAILABLE_ROOMS", value: "2A, 4C", highlight: "success" }
    ],
    appointments: [
      {
        id: "appt-2a",
        patientName: "רונית לוי",
        therapist: mockTherapists[3], // Dr. Rachel Mizrachi
        time: "14:00",
        room: "3B",
        date: new Date(2025, 11, 26)
      },
      {
        id: "appt-2b",
        patientName: "יוסי כהן",
        therapist: mockTherapists[2], // Dr. David Avraham
        time: "14:00",
        room: "3B",
        date: new Date(2025, 11, 26)
      }
    ],
    suggestedSolutions: [
      {
        id: "sol-2a",
        title: "CONFLICTS_SOLUTION_CHANGE_ROOM",
        description: "CONFLICTS_SOLUTION_CHANGE_ROOM_DESC",
        score: 98,
        isRecommended: true,
        impacts: [
          { text: "CONFLICTS_IMPACT_SAME_TIME", isPositive: true },
          { text: "CONFLICTS_IMPACT_SAME_THERAPIST", isPositive: true },
          { text: "CONFLICTS_IMPACT_SAME_FLOOR", isPositive: true }
        ]
      }
    ]
  },
  // Critical: Another therapist conflict
  {
    id: "conflict-3",
    type: "double-booking-therapist",
    severity: "critical",
    typeLabel: "CONFLICTS_TYPE_THERAPIST_DOUBLE_BOOKING",
    title: "CONFLICTS_THERAPIST_OVERLAP_TITLE",
    description: "CONFLICTS_THERAPIST_OVERLAP_DESC",
    date: new Date(2025, 11, 28), // Dec 28, 2025
    timeRange: "09:00-10:00",
    details: [
      { label: "CONFLICTS_DETAIL_DATE_TIME", value: "CONFLICTS_DETAIL_DATE_TIME_VALUE_3" },
      { label: "CONFLICTS_DETAIL_APPOINTMENT_1", value: "מיכל דוד • חדר 2B" },
      { label: "CONFLICTS_DETAIL_APPOINTMENT_2", value: "אורי שלום • חדר 4C" },
      { label: "CONFLICTS_DETAIL_TIME_UNTIL", value: "72 שעות", highlight: "warning" }
    ],
    appointments: [
      {
        id: "appt-3a",
        patientName: "מיכל דוד",
        therapist: mockTherapists[1], // Dr. Sarah Levi
        time: "09:00",
        room: "2B",
        date: new Date(2025, 11, 28)
      },
      {
        id: "appt-3b",
        patientName: "אורי שלום",
        therapist: mockTherapists[1], // Dr. Sarah Levi
        time: "09:00",
        room: "4C",
        date: new Date(2025, 11, 28)
      }
    ],
    suggestedSolutions: [
      {
        id: "sol-3a",
        title: "CONFLICTS_SOLUTION_MOVE_TO_AVAILABLE",
        description: "CONFLICTS_SOLUTION_MOVE_TO_AVAILABLE_DESC",
        score: 92,
        isRecommended: true,
        impacts: [
          { text: "CONFLICTS_IMPACT_SAME_TIME", isPositive: true },
          { text: "CONFLICTS_IMPACT_EXPERIENCED_THERAPIST", isPositive: true }
        ]
      }
    ]
  },
  // High: Holiday appointments
  {
    id: "conflict-4",
    type: "holiday-appointment",
    severity: "high",
    typeLabel: "CONFLICTS_TYPE_HOLIDAY_APPOINTMENT",
    title: "CONFLICTS_HOLIDAY_APPOINTMENTS_TITLE",
    description: "CONFLICTS_HOLIDAY_APPOINTMENTS_DESC",
    date: new Date(2025, 11, 27), // Dec 27, 2025 (Saturday)
    affectedCount: 5,
    details: [
      { label: "CONFLICTS_DETAIL_DATE", value: "CONFLICTS_DETAIL_DATE_VALUE_4" },
      { label: "CONFLICTS_DETAIL_APPOINTMENT_COUNT", value: "5" },
      { label: "CONFLICTS_DETAIL_THERAPISTS_INVOLVED", value: "3" }
    ],
    appointments: [],
    suggestedSolutions: [
      {
        id: "sol-4a",
        title: "CONFLICTS_SOLUTION_MOVE_TO_SUNDAY",
        description: "CONFLICTS_SOLUTION_MOVE_TO_SUNDAY_DESC",
        score: 92,
        isRecommended: true,
        impacts: [
          { text: "CONFLICTS_IMPACT_SAME_THERAPISTS", isPositive: true },
          { text: "CONFLICTS_IMPACT_SAME_TIMES", isPositive: true },
          { text: "CONFLICTS_IMPACT_ONE_DAY_LATER", isPositive: false }
        ]
      }
    ]
  },
  // High: Equipment conflict
  {
    id: "conflict-5",
    type: "equipment-conflict",
    severity: "high",
    typeLabel: "CONFLICTS_TYPE_EQUIPMENT_CONFLICT",
    title: "CONFLICTS_EQUIPMENT_CONFLICT_TITLE",
    description: "CONFLICTS_EQUIPMENT_CONFLICT_DESC",
    date: new Date(2025, 11, 29), // Dec 29, 2025
    timeRange: "11:00-12:00",
    details: [
      { label: "CONFLICTS_DETAIL_DATE_TIME", value: "CONFLICTS_DETAIL_DATE_TIME_VALUE_5" },
      { label: "CONFLICTS_DETAIL_EQUIPMENT", value: "CONFLICTS_EQUIPMENT_BIOFEEDBACK" },
      { label: "CONFLICTS_DETAIL_SESSIONS_NEED", value: "2" }
    ],
    appointments: [],
    suggestedSolutions: [
      {
        id: "sol-5a",
        title: "CONFLICTS_SOLUTION_STAGGER_SESSIONS",
        description: "CONFLICTS_SOLUTION_STAGGER_SESSIONS_DESC",
        score: 85,
        isRecommended: true,
        impacts: [
          { text: "CONFLICTS_IMPACT_BOTH_GET_EQUIPMENT", isPositive: true },
          { text: "CONFLICTS_IMPACT_SLIGHT_TIME_CHANGE", isPositive: false }
        ]
      }
    ]
  },
  // Medium: Treatment gap
  {
    id: "conflict-6",
    type: "treatment-gap",
    severity: "medium",
    typeLabel: "CONFLICTS_TYPE_TREATMENT_GAP",
    title: "CONFLICTS_TREATMENT_GAP_TITLE",
    description: "CONFLICTS_TREATMENT_GAP_DESC",
    date: new Date(2025, 11, 26), // Next session date
    details: [
      { label: "CONFLICTS_DETAIL_PATIENT", value: "שרה לוי" },
      { label: "CONFLICTS_DETAIL_PROGRAM", value: "CBT - 12 מפגשים" },
      { label: "CONFLICTS_DETAIL_LAST_SESSION", value: "05/12/2025" },
      { label: "CONFLICTS_DETAIL_NEXT_SESSION", value: "26/12/2025 (21 ימים!)", highlight: "danger" }
    ],
    appointments: [],
    suggestedSolutions: [
      {
        id: "sol-6a",
        title: "CONFLICTS_SOLUTION_ADD_SESSION",
        description: "CONFLICTS_SOLUTION_ADD_SESSION_DESC",
        score: 88,
        isRecommended: true,
        impacts: [
          { text: "CONFLICTS_IMPACT_PROPER_CONTINUITY", isPositive: true },
          { text: "CONFLICTS_IMPACT_SAME_THERAPIST", isPositive: true }
        ]
      }
    ]
  },
  // Medium: Another treatment gap
  {
    id: "conflict-7",
    type: "treatment-gap",
    severity: "medium",
    typeLabel: "CONFLICTS_TYPE_TREATMENT_GAP",
    title: "CONFLICTS_TREATMENT_GAP_TITLE_2",
    description: "CONFLICTS_TREATMENT_GAP_DESC_2",
    date: new Date(2025, 11, 30),
    details: [
      { label: "CONFLICTS_DETAIL_PATIENT", value: "יעקב כהן" },
      { label: "CONFLICTS_DETAIL_PROGRAM", value: "EMDR - 8 מפגשים" },
      { label: "CONFLICTS_DETAIL_LAST_SESSION", value: "10/12/2025" },
      { label: "CONFLICTS_DETAIL_NEXT_SESSION", value: "30/12/2025 (20 ימים!)", highlight: "warning" }
    ],
    appointments: [],
    suggestedSolutions: [
      {
        id: "sol-7a",
        title: "CONFLICTS_SOLUTION_ADD_SESSION_2",
        description: "CONFLICTS_SOLUTION_ADD_SESSION_DESC_2",
        score: 86,
        isRecommended: true,
        impacts: [
          { text: "CONFLICTS_IMPACT_PROPER_CONTINUITY", isPositive: true },
          { text: "CONFLICTS_IMPACT_SAME_THERAPIST", isPositive: true }
        ]
      }
    ]
  }
];
